import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { SharedAPI, UserApi } from '@shared-features/services';
import { LocationAPI } from '@shared-features/services/location-api.service';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { RoleAPI } from '@shared-features/services/role-api.service';
import { Country } from '@shared-features/models';
import { BusinessType, LocationType, LookupType, ShippingType, UserType } from '@shared-features/enums/shared-enums.enum';
import { LocationModel } from '@features/customers/models/location.model';
import { User } from '@features/users/models/user.model';
import { mapLocationValidator, noWhitespaceValidator } from '@shared/utils/form-validators.util';
import { parseMapLocation } from '@shared/utils/parse-map-location.util';
import { IconType } from '@shared/enums';

export interface AddLocationDialogData {
  customer: any;
  city?: { id: number; name: string };
  isBiddingTemplate?: boolean;
}
@Component({
  selector: 'app-add-location-dialog',
  templateUrl: './add-location-dialog.component.html',
  styleUrls: ['./add-location-dialog.component.scss'],
})
export class AddLocationDialogComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  countries: Country[] = [];
  contactsExpanded = true;
  googleMapLoaded = false;
  hasLocationPermission = true;

  latitude: number;
  longitude: number;
  address = '';
  zoom = 12;

  isDetecting = false;
  urlLocationFound = false;

  private geoCoder: any;
  private defaultRoleId: number;
  protected IconType = IconType;
  protected readonly LookupType = LookupType;
  protected readonly cityParams = { status: 'ACTIVE', businessType: BusinessType.B2B, includeCountryName: true };

  get contactsArray(): FormArray {
    return this.form.get('contacts') as FormArray;
  }

  get ownerId(): number {
    return Number(this.data?.isBiddingTemplate ? this.data?.customer?.parentId : this.data?.customer?.id);
  }

  // Compared against null rather than truthiness: a pasted pair may legitimately carry a 0, which
  // reads as "no pin" to a Boolean() check and would silently drop the point.
  get hasPin(): boolean {
    return this.latitude != null && this.longitude != null;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddLocationDialogData,
    private dialogRef: MatDialogRef<AddLocationDialogComponent, LocationModel>,
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private locationsApiService: LocationsApiService,
    private locationAPI: LocationAPI,
    private sharedAPI: SharedAPI,
    private roleAPI: RoleAPI,
    private userApi: UserApi
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      searchAddress: [{ value: '', disabled: true }],
      // Validated from the start rather than attached on demand: the validator passes an empty
      // value, so a location placed through search is never blocked by an untouched link field.
      link: ['', [mapLocationValidator]],
      name: ['', [Validators.required, noWhitespaceValidator]],
      // The create call rejects a location with no city, so this is required. Seeded when the
      // caller already knows the city (the bidding flow picks one before opening the dialog).
      city: [this.data?.city ? { id: this.data.city.id, value: this.data.city.name } : null, [Validators.required]],
      contacts: this.formBuilder.array([this.newContactRow()]),
    });

    this.form
      .get('searchAddress')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((place: any) => {
        // The two ways of placing the pin are mutually exclusive: picking a place from the
        // autocomplete clears whatever was pasted, and Detect clears the search below.
        this.form.get('link').reset('', { emitEvent: false });
        this.urlLocationFound = false;

        this.applyPlace(place);
      });

    this.form
      .get('link')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // The banner names the coordinates the last Detect found, so it must not outlive the value
        // it read them from. The pin goes with it, but only when Detect is what placed it — a pin
        // dropped by search or by dragging the marker is not this field's to clear.
        if (this.urlLocationFound) {
          this.urlLocationFound = false;
          this.clearPin();
        }
      });

    this.locationsApiService.loadGoogleMapsApi().then(() => {
      this.googleMapLoaded = true;
      this.geoCoder = new (window as any).google.maps.Geocoder();
      this.form.get('searchAddress').enable({ emitEvent: false });
      this.setCurrentLocation();
      this.changeDetectorRef.markForCheck();
    });

    navigator.permissions?.query({ name: 'geolocation' as PermissionName }).then((status) => {
      if (status.state === 'denied') {
        this.hasLocationPermission = false;
        this.asyncFeedbackService.showFailureMessage(this.translate('ErrorMessages.MapErrorLoading'));
        this.changeDetectorRef.markForCheck();
      }
    });

    // Loaded once for every contact row's phone field, and for the role the created users need.
    this.load(forkJoin([this.sharedAPI.getAllCountries(), this.roleAPI.getActiveRoles(UserType.CUSTOMER)]))
      .pipe(takeUntil(this.destroy$))
      .subscribe(([countries, roles]) => {
        this.countries = countries;
        this.defaultRoleId = (roles.find((role) => /contact/i.test(role.name)) ?? roles[0])?.id;
        this.changeDetectorRef.markForCheck();
      });
  }

  toggleContacts(): void {
    this.contactsExpanded = !this.contactsExpanded;
  }

  addAnotherContact(): void {
    this.contactsArray.push(this.newContactRow());
  }

  removeContact(index: number): void {
    this.contactsArray.removeAt(index);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // Both the marker drag and a click on the map land here; the pin is the single source of truth
  // for the coordinates, and the address is re-resolved from it.
  onMapPointChanged(event: google.maps.MapMouseEvent): void {
    if (!event?.latLng) {
      return;
    }
    this.latitude = event.latLng.lat();
    this.longitude = event.latLng.lng();
    this.resolveAddress();
  }

  // One button for both shapes the point arrives in — a maps URL or a pasted `lat, lng` pair.
  // `parseMapLocation` tells them apart, so the user never has to pick the matching field.
  detectMapURL(): void {
    const link = this.form.get('link');
    this.urlLocationFound = false;
    // Marking it touched is what puts the validator's message on screen; the validator decides
    // which of the three failures to name, so nothing here reports the error a second time.
    link.markAsTouched();

    // Nothing to read. Emptying the field has already dropped any pin Detect placed, via the
    // `link` subscription above, and a pin placed by search or by dragging is not ours to clear.
    if (!link.value?.trim()) {
      return;
    }

    this.form.get('searchAddress').reset('', { emitEvent: false });
    const point = parseMapLocation(link.value);

    if (!point) {
      this.clearPin();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.latitude = point.latitude;
    this.longitude = point.longitude;
    this.zoom = 15;
    this.urlLocationFound = true;
    // The reverse geocode is what fills the address, so the spinner runs until it answers.
    this.isDetecting = true;
    this.resolveAddress(() => (this.isDetecting = false));
    this.changeDetectorRef.markForCheck();
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.hasPin) {
      this.asyncFeedbackService.showFailureMessage(this.translate('ErrorMessages.EmptyLongAndLat'));
      return;
    }

    const contacts = this.filledContacts();
    // The location create takes contact ids, so any inline contact has to exist first.
    const contacts$ = contacts.length
      ? forkJoin(contacts.map((contact) => this.userApi.addUser(...this.userArgs(contact))))
      : of([] as User[]);

    this.load(
      contacts$.pipe(
        switchMap((users: User[]) => {
          const usersIds = (users || []).map((user) => user?.id).filter(Boolean);
          return this.locationAPI.addLocation(this.buildLocation(), this.ownerId, usersIds, usersIds[0]);
        })
      ),
      { successMessagePath: 'SuccessMessages.LocationModifiedSuccessfully' }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((location) => {
        // The caller reads `city?.id`/`cityId` off the result — they drive the spot-rate lookup.
        // The server response does not always carry the city back, so fall back to the picked one.
        const city = this.form.value.city;
        if (!location.city && city) {
          location.city = { id: city.id, name: city.value } as any;
        }
        this.dialogRef.close(location);
      });
  }

  private buildLocation(): LocationModel {
    return Object.assign(new LocationModel(), {
      type: LocationType.Customer,
      title: this.form.value.name?.trim(),
      address: this.address,
      cityId: this.form.value.city?.id,
      latitude: this.latitude,
      longitude: this.longitude,
      shippingType: ShippingType.All,
    });
  }

  private userArgs(contact: { fullName: string; phone: { countryCode: string; mobileNumber: string } }): [
    User,
    UserType,
    number,
    number[]
  ] {
    const [firstName, ...rest] = contact.fullName.trim().split(/\s+/);
    const user = Object.assign(new User(), {
      firstName,
      lastName: rest.join(' ') || firstName,
      mobileNumber: `${contact.phone?.countryCode}-${contact.phone?.mobileNumber}`,
    });
    return [user, UserType.CUSTOMER, this.ownerId, this.defaultRoleId ? [this.defaultRoleId] : []];
  }

  private filledContacts(): any[] {
    return this.contactsArray.value.filter(
      ({ fullName, phone }) => Boolean(fullName?.trim()) && Boolean(phone?.mobileNumber)
    );
  }

  // The whole contact block is optional, so an untouched row is valid. A half-filled one is not —
  // otherwise it would be dropped on save without the user noticing.
  private newContactRow(): FormGroup {
    return this.formBuilder.group(
      {
        fullName: ['', [noWhitespaceValidator]],
        phone: [null],
      },
      { validators: [AddLocationDialogComponent.contactRowValidator] }
    );
  }

  private static contactRowValidator(group: AbstractControl): ValidationErrors | null {
    const { fullName, phone } = group.value ?? {};
    const hasName = Boolean(fullName?.trim());
    const hasNumber = Boolean(phone?.mobileNumber);
    if (hasName === hasNumber) {
      return null;
    }
    return { incompleteContact: true };
  }

  private applyPlace(place: any): void {
    if (place?.geometry) {
      this.latitude = place.geometry.location.lat();
      this.longitude = place.geometry.location.lng();
      this.address = place.formatted_address;
      this.zoom = 15;
    } else {
      this.clearPin();
    }
    this.changeDetectorRef.markForCheck();
  }

  private clearPin(): void {
    this.latitude = null;
    this.longitude = null;
    this.address = '';
  }

  private setCurrentLocation(): void {
    if (!('geolocation' in navigator)) {
      return;
    }
    navigator.geolocation.getCurrentPosition((position) => {
      this.latitude = position.coords.latitude;
      this.longitude = position.coords.longitude;
      // The opening view of wherever the user is standing. `resolveAddress` used to overwrite this
      // with 12 once the geocode answered, so 12 is what the dialog has always actually opened at.
      this.zoom = 12;
      this.resolveAddress();
    });
  }

  // `onDone` fires on every exit, geocode failures included, so a caller showing progress always
  // gets to stop it. The zoom is the caller's to set: reverse geocoding a point says nothing about
  // how closely the user wants to look at it, and resetting it here undid Detect's zoom-in and
  // threw away the zoom the user had chosen every time they dragged the marker.
  private resolveAddress(onDone?: () => void): void {
    if (!this.geoCoder || !this.hasPin) {
      onDone?.();
      return;
    }
    this.geoCoder.geocode({ location: { lat: this.latitude, lng: this.longitude } }, (results, status) => {
      // Maps JS fires its callbacks outside Angular's zone, so without this the assignments above
      // never reach the view — the spinner would keep spinning. Same guard the Places callbacks in
      // `AutocompleteGoogleMapsComponent` use.
      this.ngZone.run(() => {
        if (status === 'OK' && results?.[0]) {
          this.address = results[0].formatted_address;
        }
        onDone?.();
        this.changeDetectorRef.markForCheck();
      });
    });
  }
}
